/**
 * PeekDialog — THE large content-dialog standard: one chrome for every modal
 * that lets you inspect something in place and expand to its full page (the
 * object quick-view, the Pulse insight drill — any future "view it here"
 * surface adopts this, never a bespoke Dialog).
 *
 * v2 grammar (Matt, 2026-07-20 — the "Split · Calibrated" pick from the
 * /styleguide/object-peek/lab study):
 *   · the panel surface is `bg-background` — the SITE background, so every
 *     page recipe (Card, StatCard, DataTable, badges) renders 1:1 inside the
 *     modal. Depth comes from the border + shadow, never a lighter fill
 *     (`bg-popover` inverted the surface ladder: a Card sat darker than its
 *     substrate);
 *   · the header is the peek masthead — a KIND BAR (kind glyph + `type-subtext` caps
 *     noun, sharing one centre line with the Expand/Close pair) over a `type-heading`
 *     title (Radix Title/Description kept underneath for a11y). The kind bar
 *     answers "what am I looking at, and what is it inside of" — a child object
 *     spells its parent there (`Apple → Lead`) and the parent is a link. The
 *     context line must be NET-NEW information; it never restates a fact the
 *     body shows (status, owner, a vital). NO tags in the title block, ever —
 *     state lives in a labeled body slot (a Fields "Status" row, a StatCard
 *     `leadingDot`);
 *   · Expand + Close sit TOGETHER top-right as a ghost icon pair drawn as a
 *     MATCHED set on one 16-grid (equal optical extent + equal stroke — mixed
 *     lucide glyphs read ~50% apart because X spans 12/24 of its viewbox and
 *     Maximize2 spans 18/24);
 *   · A GLYPH THAT STANDS IN FOR A WORD SAYS THAT WORD ON HOVER, through the
 *     house `IconTooltip` and never a native `title=` (Matt, 2026-07-22: "when
 *     we have icons that represent something we need to be able to hover over
 *     the icon and get that help message that describes what that icon means").
 *     STANDS IN FOR is the whole test, and it is one-fact-one-place again: a
 *     property chip earns a bubble because the row traded its label column away
 *     for a 14px glyph, and an icon-only control earns one because it has no
 *     visible name at all — but the timeline glyph does NOT, because its verb is
 *     already written in the sentence beside it, and neither does the masthead
 *     kind badge, whose noun sits 8px to its right. A bubble that repeats an
 *     adjacent word is not help, it is an occlusion;
 *   · one spacing cadence — 12 inside a section (label→content), 16 between
 *     timeline rows, 24 between sidebar groups, 32 between main sections;
 *   · sections are headed by the house caps `SectionLabel` (case marks a
 *     heading, not weight) via `PeekDialogSection`;
 *   · pass `sidebar` for the split arrangement (a main column + a `w-72` rail;
 *     the panel widens to `sm:max-w-4xl`). WHAT GOES WHERE IS THE CALLER'S
 *     CALL, and for objects it INVERTED (Matt, 2026-07-21: "the properties are
 *     the main point of that modal, not the timeline"): the main column now
 *     carries the object's substance — its narrative when it has one, then its
 *     editable properties — and the rail carries Activity. This block stays
 *     agnostic; see `object-peek.tsx` for the composition;
 *   · pass `footer` for the STATIC bottom bar. It sits OUTSIDE the scroll
 *     region, so it is always on screen no matter how long the body runs — the
 *     home for the object's actions (right) and one quiet write-status line
 *     (left). Actions used to pin to the bottom of the RAIL via `mt-auto`,
 *     which put the CTA in a 288px column halfway up a tall peek.
 */

import * as React from "react";
import { RouterLink as NextLink } from "../router";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../dialog";
import { Button } from "../button";
import { IconTooltip } from "./icon-tooltip";
import { SectionLabel } from "./section";
import { IntentBadge } from "./status-badge";
import { IntentDot } from "./status-dot";
import {
  ArrowRightCircle,
  CircleCheck,
  CircleDot,
  CirclePlus,
  CircleX,
  MessageSquare,
  PencilLine,
  UserRound,
} from "../icons";
import { formatRelativeTime } from "../format";
import { cn } from "../utils";
import type { Intent } from "../tokens";

/* ── The matched Expand / Close glyph pair (one 16-grid, equal extent) ────── */

export function PeekGlyphExpand(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M9.75 3.25h3v3M12.75 3.25 9.25 6.75M6.25 12.75h-3v-3M3.25 12.75l3.5-3.5" />
    </svg>
  );
}

export function PeekGlyphClose(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      aria-hidden
      {...props}
    >
      <path d="m4 4 8 8M12 4l-8 8" />
    </svg>
  );
}

/* ── The dialog ──────────────────────────────────────────────────────────── */

export interface PeekDialogProps {
  open: boolean;
  onClose: () => void;
  /** The subject's identity — rendered as the masthead (Radix `Dialog.Title`). */
  title: React.ReactNode;
  /**
   * One muted context line under the title (Radix `Dialog.Description`). Must
   * be NET-NEW information — never a restatement of a fact the body shows.
   */
  description?: React.ReactNode;
  /**
   * The caps kicker on the kind bar — what this object IS ("Task"), and where it
   * lives when it is a child ("Apple → Lead", the parent clickable). Renders
   * BESIDE `icon`, no longer instead of it.
   */
  eyebrow?: React.ReactNode;
  /**
   * The kind glyph leading the kind bar (Matt, 2026-07-21: "the icons at the top
   * need to be aligned with the title of the thing being edited"). Keep it at or
   * under 28px — it shares a centre line with the ghost controls, and a taller
   * box grows the masthead rather than reading as part of that row.
   *
   * Icon and eyebrow are INDEPENDENT: either, both, or neither. A consumer that
   * passes only one is unchanged by the pairing.
   */
  icon?: React.ReactNode;
  /**
   * The full-page destination. When set, the Expand control renders beside
   * Close; when absent the dialog is view-in-place only.
   */
  expandHref?: string;
  /** Accessible label + tooltip for Expand. Default "Open full page". */
  expandLabel?: string;
  /**
   * Fired when Expand is clicked, alongside the navigation. Pass the close
   * handler when the dialog's host outlives the route (a persistent rail), so
   * expanding never leaves a modal stranded over the destination page.
   */
  onExpand?: () => void;
  /**
   * The property rail for the split arrangement: `PeekDialogFields` groups
   * (wrapped in `PeekDialogSection`s) ordered by importance, optionally ending
   * in an action row. When present the panel widens and the body splits; the
   * main column and the rail scroll together.
   */
  sidebar?: React.ReactNode;
  /**
   * The STATIC bottom bar, held outside the scroll region so it never leaves
   * the screen. Two slots by convention (see `PeekDialogFooter`): a quiet
   * status line on the left and the object's buttons on the right. The buttons
   * stay even when every edit auto-saves — "we always want the buttons"
   * (Matt, 2026-07-21) — because a dialog with no visible way out reads
   * unfinished, and the status line is what makes an invisible write legible.
   */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** Extra classes on the panel (`DialogContent`). */
  className?: string;
  /** Extra classes on the main content column. */
  bodyClassName?: string;
}

/**
 * Is a caret actually sitting in this element? — the whole test behind the
 * two-stage Escape on `PeekDialog` (see the `onEscapeKeyDown` note there).
 *
 * The non-text input types are listed as the EXCLUSION rather than listing the
 * text ones as the inclusion, because `type` defaults to "text" and an unknown
 * or future value degrades to "treat it as text", which is the safe direction:
 * the cost of a wrong `true` is one extra Escape, the cost of a wrong `false` is
 * a discarded edit and a closed dialog.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function isTextEntry(el: HTMLElement | null): boolean {
  if (el == null) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName === "INPUT") {
    return !NON_TEXT_INPUT_TYPES.has((el as HTMLInputElement).type);
  }
  return false;
}

export function PeekDialog({
  open,
  onClose,
  title,
  description,
  eyebrow,
  icon,
  expandHref,
  expandLabel = "Open full page",
  onExpand,
  sidebar,
  footer,
  children,
  className,
  bodyClassName,
}: PeekDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        /*
         * A peek OPENS TO BE READ, so nothing inside it is auto-focused.
         * Radix's default is to focus the first tabbable descendant, and once
         * the masthead became an editable heading that was the title itself:
         * every peek opened with a focus ring boxed around its own name, which
         * is precisely the "input chrome" the editable title was reshaped to
         * avoid — and it implied a rename was already in progress.
         *
         * Focus is then moved to the PANEL itself rather than simply left
         * behind: preventDefault alone strands it on whatever was clicked
         * (measured: `document.body`), which costs a screen reader the dialog
         * announcement. The panel carries tabIndex -1, so focusing it announces
         * the dialog, keeps Escape working, and starts Tab at the top of the
         * body — all without putting a caret in the object's name.
         */
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
        /*
         * ESCAPE LEAVES THE FIELD BEFORE IT LEAVES THE PEEK.
         *
         * Every editable surface in here already handles Escape correctly and
         * none of them could be observed doing it: Radix's `DismissableLayer`
         * registers its Escape handler on the DOCUMENT in the CAPTURE phase
         * (`@radix-ui/react-use-escape-keydown`), so it won the key before any
         * handler in the subtree ran — the field reverted, then the dialog
         * unmounted and took the revert (and every other unsaved draft) with it.
         * `stopPropagation` is useless against a capture-phase document
         * listener; THIS is the seam, and it is the one the three call sites in
         * `object-fields/` were each told to wait for.
         *
         * `preventDefault` here cancels only the DISMISS — DismissableLayer
         * dismisses on `!event.defaultPrevented` — and it does NOT stop
         * propagation, so the very same keystroke still reaches the focused
         * element and runs its own handler on the way back up: the prose
         * textarea closes, the contentEditable title restores + blurs, a typed
         * control reverts its draft. One key, two stages, no new state: the
         * DOM already knows whether a caret is in a field.
         *
         * Guarded on the ELEMENT, never on `role`: the description's READ mode
         * is a `role="textbox"` div (it must still close the peek), while the
         * title is a contentEditable span that is always editable and only
         * matters when focused. So: contentEditable, textarea, or a text-ish
         * input. A checkbox or a button has no field-level Escape to offer, and
         * swallowing the key there would strand the user in a modal.
         *
         * An open Select/Popover never reaches this — it is a higher layer, and
         * DismissableLayer only calls the topmost layer's handler.
         */
        onEscapeKeyDown={(e) => {
          if (isTextEntry(e.target as HTMLElement | null)) e.preventDefault();
        }}
        // No description → silence Radix's missing-Description warning; with
        // one, Radix wires aria-describedby to the DialogDescription itself.
        {...(description == null ? { "aria-describedby": undefined } : {})}
        className={cn(
          "flex max-h-[85vh] flex-col gap-0 bg-background p-0",
          sidebar ? "sm:max-w-4xl" : "sm:max-w-3xl",
          className,
        )}
      >
        {/* Masthead: eyebrow + title left, the Expand+Close pair right. The
            negative margins seat the ghost pair on the px-6 content edge.

            NO RULE UNDER IT (Matt, 2026-07-21: "we don't need the header line
            or the footer line"). The line was doing real work — it was the only
            mark that the body scrolls under a fixed masthead — so the SPACE has
            to do that work instead, which is why `pb-5` stays and the body opens
            on its own `py-6`: 44px of clear air between the title and the first
            section label. A rule is one way to separate two regions; enough
            space is the quieter one, and the masthead is now an editable
            heading, which a boxed-in region reads wrong on. */}
        {/* `pb-2` (8px) is the TITLE→DESCRIPTION gap, and it is the only thing
            producing it — the body's top padding is now `pt-0` so this seam has a
            single owner instead of two paddings summing to a number neither
            component chose. 8px matches the reference
            (/styleguide/workflow-detail), where the description sits right under
            the title as part of one identity block.

            It lives on the MASTHEAD rather than inside the scroll container on
            purpose: masthead padding is fixed, so the gap holds while the body
            scrolls. As `pt` on the scrollable body it would scroll away and let
            the first line ride up against the title. */}
        <div className="flex shrink-0 flex-col px-6 pt-6 pb-2">
          {/* THE KIND BAR — glyph + noun on ONE line with Expand/Close (Matt,
              2026-07-22: "that icon box needs to be smaller and we need the name
              of the object ... vertically inline with the icons to
              close/expand").

              The glyph used to sit on its own line above the title, holding the
              eyebrow's slot on the reasoning that "an icon and a caps kind label
              are the same fact twice". That reasoning is retired: a glyph says
              WHICH KIND only to someone who has already learned the map, the
              noun says it to everyone, and pairing them buys the row a second
              job — it is where an object states its PARENT (`Apple → Lead`),
              which no glyph can spell. Both render together now.

              `items-center` on this row (not `items-start`) is what makes
              "vertically inline" true: the 24px glyph, the caps noun and the
              two 28px ghost buttons share one centre line. The buttons keep
              their negative right margin to sit on the px-6 content edge, but
              the vertical nudge is gone — centring is doing that work now. */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {icon != null ? <span className="shrink-0">{icon}</span> : null}
              {/* NATURAL CASE, not the house caps eyebrow (Matt, 2026-07-22).
                  This slot stopped being a pure label the moment it started
                  carrying a parent's NAME: `uppercase` is a typographic mark for
                  category text, and applied to user data it destroys real
                  information — "Globex Labs" reads GLOBEX LABS, and "eBay",
                  "McKinsey", "iOS" all come out wrong in a way no reader can
                  undo. The glyph and the muted color already say "this is the
                  label row", so the caps were the one signal here that had to
                  lie about the content to do its job.

                  `type-subtext` (up from `type-subtext`) and no `tracking-wide` because
                  both were caps-only devices: 10px letterspaced text is legible
                  as five capitals and not as a company name. */}
              {eyebrow != null ? (
                <span className="min-w-0 truncate type-subtext leading-ui text-muted-foreground">
                  {eyebrow}
                </span>
              ) : null}
            </div>
            {/* THESE TWO ARE NAMELESS BY CONSTRUCTION — a stroke glyph and
                nothing else — so they are the clearest case for a tooltip, and
                what they carried was `title=`: unstyled, ~1s late, and drawn by
                the OS rather than by the app, so the peek's only two labeled
                icons answered in a different voice from every other icon in the
                shell. `IconTooltip` is the house instrument (see its own note:
                "never rely on the native `title=` attribute").

                `side="bottom"`, against the block's default of "top", because
                these sit ON the panel's top edge: a bubble above them lands
                outside the modal, floating over the dimmed page as if it had
                escaped. Downward it stays in the panel, in the masthead's own
                clear air. Every tooltip in a peek points INWARD. */}
            <div className="-mr-1.5 flex shrink-0 items-center gap-1">
              {expandHref ? (
                <IconTooltip label={expandLabel} side="bottom">
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    aria-label={expandLabel}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <NextLink href={expandHref} onClick={onExpand}>
                      <PeekGlyphExpand />
                    </NextLink>
                  </Button>
                </IconTooltip>
              ) : null}
              {/* Tooltip ▸ DialogClose ▸ Button, all `asChild` — the same
                  collapse-onto-one-element composition `IconTooltip` documents
                  for a menu/popover trigger. */}
              <IconTooltip label="Close" side="bottom">
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Close"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <PeekGlyphClose />
                  </Button>
                </DialogClose>
              </IconTooltip>
            </div>
          </div>
          {/* The title block now owns the full panel width — nothing sits to its
              right, so a long name wraps under the controls instead of against
              them.

              `pt-4` (16px), up from `pt-2` (Matt, 2026-07-22: "increase the
              padding between the icon at the top and the title — that one is
              weird"). At 8px the kind bar and the title were closer to each other
              than the title is to its own description, so the type label read as
              part of the heading instead of as the quiet line above it. 16px is
              also what the reference page puts between its back-link and its
              title, and it makes the masthead ladder descend cleanly: 16 above
              the title, 8 below it. */}
          <DialogHeader className="min-w-0 gap-2 pt-4 text-left">
            <DialogTitle className="text-balance type-title font-medium leading-display tracking-tight text-foreground">
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription className="type-body leading-body text-muted-foreground">
                {description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
        </div>

        {sidebar ? (
          /* Split body — one scroll region. NO rule between the columns (Matt,
             2026-07-21: "we need no line next to activity"): the rail is the
             secondary read, and a hairline framed it as a separate pane rather
             than a quieter continuation of the same body. The `pl-2` keeps the
             activity column off the main content without drawing a line to do
             it — space separates the two regions, the way the masthead lost its
             rule for the same reason. */
          <div className="flex min-h-0 flex-1 overflow-y-auto">
            <div
              className={cn(
                "flex min-w-0 flex-1 flex-col gap-8 px-6 py-6",
                bodyClassName,
              )}
            >
              {children}
            </div>
            <aside className="flex w-72 shrink-0 flex-col gap-6 py-6 pr-5 pl-2">
              {sidebar}
            </aside>
          </div>
        ) : (
          <div
            className={cn(
              // `pt-0`: the masthead's `pb-2` is the sole owner of the
              // title→description seam (see the note there). Two components each
              // contributing padding to one gap is how it drifted to 28px.
              "flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pt-0 pb-6",
              bodyClassName,
            )}
          >
            {children}
          </div>
        )}

        {/* The static bottom bar. `shrink-0` + living outside the scroll region
            is what makes it stay put while the body scrolls under it — and that
            is what holds now that the hairline is gone (Matt, 2026-07-21). The
            separation is `py-4` against the body's own `py-6`: 40px between the
            last row and the buttons, which is more air than the rule was
            drawing attention with. */}
        {footer ? (
          <div className="shrink-0 px-6 py-4">{footer}</div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The two-slot bottom bar: a quiet `status` line on the left, `children` (the
 * buttons) on the right. Split so the status can never push the buttons off the
 * edge — it truncates, they don't — and so a peek with nothing to report still
 * renders its buttons in exactly the same place.
 *
 * The status slot is deliberately NOT an `<Alert>`: alerts are for conditions
 * the user has to resolve, and "Saved 3:42 PM" is the opposite of that. It is
 * one muted line with `aria-live` so a screen reader hears the write land
 * without a role change every keystroke.
 */
export function PeekDialogFooter({
  leading,
  status,
  children,
}: {
  /**
   * FAR LEFT — a destructive or otherwise irreversible action (Archive,
   * Delete), held as far as the bar allows from the primary on the right.
   *
   * It is a slot rather than "just another child" because the separation is the
   * whole point: the task peek used to keep Archive out of its property rail on
   * the grounds that it must "never be a mis-click beside a Select", and moving
   * the actions into a footer would have re-created exactly that hazard one
   * button away from Done. Left/right is the standard dialog answer.
   */
  leading?: React.ReactNode;
  /** The write-status line, beside `leading`. Absent/null ⇒ nothing renders. */
  status?: React.ReactNode;
  /** Right slot — the buttons, always rendered. */
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {leading}
        {status ? (
          <span className="min-w-0 type-subtext leading-ui text-muted-foreground">
            {status}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {children}
      </div>
    </div>
  );
}

/**
 * One titled section of a PeekDialog body or sidebar: the house caps
 * `SectionLabel` (case marks a heading, not weight) with an optional count
 * folded in and a right-aligned action, over the content on the 12px
 * label→content step. Never hand-roll an in-dialog heading.
 */
export function PeekDialogSection({
  title,
  count,
  action,
  children,
  className,
}: {
  title: React.ReactNode;
  count?: number;
  /** Right-aligned action (e.g. a "View all" link). */
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-1.5">
          <SectionLabel>{title}</SectionLabel>
          {count != null ? (
            <span className="type-subtext leading-ui text-muted-foreground">· {count}</span>
          ) : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** One label→value property row. Exactly one value form per row. */
export interface PeekField {
  label: string;
  /**
   * A leading glyph for the CHIP layout (`PeekDialogPropertyChips`) — the
   * object's-kind-of-thing this property is (a person for Owner, a calendar for
   * a date). Ignored by the read-only `PeekDialogFields` list, which is a
   * label→value table and needs no per-row icon. Absent ⇒ the chip leads with
   * its value alone (the state chip, whose intent dot is already its glyph).
   */
  icon?: React.ReactNode;
  /** Plain value (text or a node). */
  value?: React.ReactNode;
  /** Renders the value as an entity link. */
  href?: string;
  /** Renders the value as an `IntentBadge` — the home for a state tag. */
  badge?: { label: string; intent: Intent };
  /** Leading intent dot before a plain value (lifecycle/health signal). */
  intent?: Intent;
  /** Muted suffix after the value (e.g. "since 1:14 PM"). */
  hint?: string;
  /**
   * An editor for this property, rendered in place of the value. A control row
   * is NEVER dropped when empty — an unset due date must still offer its field,
   * or the property becomes unreachable. Keep the control quiet (borderless
   * until hover/focus) so a rail of editable rows still reads as a fact list.
   */
  control?: React.ReactNode;
  /**
   * The row's SECOND LINE — this block's version of `FormField`'s help/error
   * line (form-shell.tsx:203-206), rendered full-width under the label→value
   * line. Its usual content is an ephemeral write status; anything
   * sentence-shaped that belongs to the row rather than beside its value goes
   * here (an editor that has to ask for one line before it writes, say).
   *
   * It cannot ride beside the control. The rail is `w-72` (288) − `px-5` (40) =
   * 248px of content, and a row is `dt w-24` (96) + `gap-3` (12), so the value
   * column is ~140px. "Saved 3:42 PM" is ~80px of that, and a refusal the user
   * has to read ("Stage history only moves forward") is wider than the whole
   * column — the control truncated to "In…" exactly when the user had just
   * acted. A status that truncates its own message is worse than no status.
   *
   * A NODE, not a phase union: the copy, the tone and the `aria-live` belong to
   * the surface that owns the write (see `BriefFieldRow`, which composes its own
   * status text before handing it to `FormField`), never to this chrome. Absent
   * ⇒ the row renders exactly as it does today.
   *
   * Usually paired with a `control`, but not required to be: a write can LOCK
   * the row it just changed (a goal that closes stamps "Closed goals don't
   * reopen." onto the status it wrote), and the outcome of that write still has
   * to be readable. Passing a status is what makes the row wrap.
   */
  status?: React.ReactNode;
}

/**
 * PeekDialogFields — the read-only property list of the standard: hairline
 * label→value rows on the list-row cadence (`w-24` muted label, `type-body`
 * value, `py-2`, ~40% dividers). A short fact sits inline beside its label; a
 * sentence-length one breaks to a stacked full-width row so a narrow sidebar
 * never squeezes prose into a tall three-word column. This is where an
 * object's state tag, owner, dates and relationships live — labeled rows,
 * never a stat band and never chips in the title. A row with nothing to show
 * is dropped, so an empty field can't render a label-only row — unless it
 * carries a `control`, which makes the row editable and always present.
 */
export function PeekDialogFields({
  fields,
  className,
}: {
  fields: PeekField[];
  className?: string;
}) {
  const rows = fields.filter(
    (f) =>
      f.control != null || f.badge != null || (f.value != null && f.value !== ""),
  );
  if (!rows.length) return null;
  /**
   * "THE INPUT SECTION" — a list that actually contains editors, which is the
   * thing Matt asked to give 20px of air above and below (2026-07-21). Derived
   * rather than passed as a prop because it is not a caller's preference: a
   * list of editors needs room to be operated in, a list of facts needs to stay
   * dense enough to scan, and which one this is, is already knowable from the
   * rows. It also keeps every read-only Fields list in the app — the styleguide
   * peek, the Related group under Properties — rendering byte-identically.
   */
  const isInputSection = rows.some((f) => f.control != null);
  return (
    // No section-level vertical padding: the editable list is now one section
    // among several (glance chips above, Activity below) separated by the body's
    // own gap, so its own `py-5` (a holdover from when it was the whole
    // Properties block) doubled up into a too-airy gap. Row rhythm + the section
    // gap carry the spacing now.
    <dl className={cn("flex flex-col", className)}>
      {rows.map((f, i) => {
        // A control sets its own height, so the row centers on it rather than
        // sharing a text baseline; prose-length values still stack full-width.
        const long =
          f.control == null && typeof f.value === "string" && f.value.length > 34;
        // A status line is a second full-width <dd> wrapping under the
        // label→value line — valid `dl` grouping (dt, dd, dd) and the same
        // full-width stacking this block already does for a `long` value.
        const hasStatus = f.status != null && f.status !== false && f.status !== "";
        return (
          <div
            key={`${f.label}-${i}`}
            className={cn(
              "border-b border-border/40 py-2.5 first:pt-0 last:border-b-0",
              // `first:pt-0` + `last:pb-0` make the list's own `py-5` the WHOLE
              // measurement: 20px of air, not 20 + the row's own 10.
              isInputSection && "last:pb-0",
              long
                ? "flex flex-col gap-1"
                : hasStatus
                  ? // The status <dd> is `w-full`, so the row MUST wrap or it
                    // takes the line and squeezes the value <dd> to 0px. That is
                    // true whether or not the row has a control — a row can be
                    // locked BY the write it is reporting — so the wrap follows
                    // the status, not the control.
                    //
                    // NOT `cn("… gap-3", "gap-y-1")`: tailwind-merge only drops
                    // an earlier `gap-x`/`gap-y` when a LATER bare `gap` lands,
                    // never the reverse, so both would survive and the 4px row
                    // gap would hang on stylesheet order. Emit the axes.
                    cn(
                      "flex flex-wrap gap-x-3 gap-y-1",
                      f.control != null ? "items-center" : "items-baseline",
                    )
                  : f.control != null
                    ? "flex items-center gap-3"
                    : "flex items-baseline gap-3",
            )}
          >
            <dt
              className={cn(
                "shrink-0 type-subtext leading-ui",
                // AN INPUT'S LABEL IS FULL CONTRAST; a fact's label stays muted
                // (Matt, 2026-07-21: "the input label should always be white").
                // Scoped to the row rather than the list, and that scoping earns
                // its keep twice: a control is deliberately quiet until you touch
                // it, so a muted label beside it left the row with nothing at
                // full contrast at all; and a Properties group that mixes
                // editable rows with resolved read-only ones (a task's Project,
                // an object's Owner) now says WHICH rows you can change before
                // you click one to find out.
                f.control != null ? "text-foreground" : "text-muted-foreground",
                !long && "w-24",
              )}
            >
              {f.label}
            </dt>
            <dd
              className={cn(
                "flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 type-body text-foreground",
                long ? "leading-body" : "flex-1",
              )}
            >
              {f.control ? (
                f.control
              ) : f.badge ? (
                <IntentBadge intent={f.badge.intent} withDot>
                  {f.badge.label}
                </IntentBadge>
              ) : (
                <>
                  {f.intent ? <IntentDot intent={f.intent} size="xs" /> : null}
                  {f.href ? (
                    <NextLink
                      href={f.href}
                      className="min-w-0 break-words text-foreground hover:underline"
                    >
                      {f.value}
                    </NextLink>
                  ) : (
                    <span className="min-w-0 break-words">{f.value}</span>
                  )}
                  {f.hint ? (
                    <span className="type-subtext text-muted-foreground">{f.hint}</span>
                  ) : null}
                </>
              )}
            </dd>
            {hasStatus ? <dd className="w-full">{f.status}</dd> : null}
          </div>
        );
      })}
    </dl>
  );
}

/**
 * PeekDialogPropertyChips — the properties as ONE inline, wrapping row of chips
 * under a single left label, the way Linear's panels read (Matt, 2026-07-21,
 * with a reference image: "it should look like this" — Properties on the left,
 * `[icon] value` chips flowing to the right; "things that have no input like
 * 'Owner' need to be inline under the description").
 *
 * What it replaces for the Properties section: a `PeekDialogFields` table, one
 * stacked label→value row per property. That table is still exactly right for a
 * READ-ONLY fact list (it stays for Related, and for the styleguide preview) —
 * chips are for the property surface a caller wants to read at a glance and edit
 * in place.
 *
 * Every property renders, set or not (unlike the table, which drops an empty
 * read-only row): an unset Owner is the chip that says "Owner", the affordance
 * to set one. An editable field's own control carries its placeholder; a
 * read-only field shows its label muted when it has no value.
 *
 * Per-field write status (a gated option's note line, a refusal, "Saving…")
 * cannot sit inside a chip in a wrapping row without breaking the row, so it
 * stacks FULL-WIDTH beneath the chips — the same reasoning that gave the table
 * its second-line `status` slot, applied to a row that flows horizontally. The
 * footer still carries the one aggregate line.
 */
export function PeekDialogPropertyChips({
  label,
  fields,
}: {
  /** The single left label ("Properties", "Related"). Omit for label-less chips. */
  label?: string;
  fields: PeekField[];
}) {
  // Editable rows (a `control`) always render; a read-only row renders whenever
  // it has a value OR a label to offer as its unset affordance — which is every
  // row, so the only thing dropped is a genuinely nameless one.
  const rows = fields.filter((f) => f.control != null || f.label);
  if (!rows.length) return null;
  const statuses = rows.filter(
    (f) => f.status != null && f.status !== false && f.status !== "",
  );
  const chips = (
    <div className="flex flex-col gap-1.5">
      {/* `-ml-1.5` CANCELS THE FIRST CHIP'S OWN PADDING so the row's text starts
          on the panel gutter like everything else. A PropertyChip is `h-7 px-1.5`
          — that 6px is the hover fill's breathing room, and it was also silently
          indenting the whole properties row: measured, every other block in the
          peek (title, description, section labels, timeline glyphs) begins at
          24px and "In progress" began at 30px. Six pixels is small enough to read
          as sloppiness rather than intent, which is the worst amount to be off by.

          Negative margin, not `px-0`: the fill still has to extend left of the
          glyph on hover, so the box keeps its padding and the ROW moves instead.
          Same device the masthead controls use on the right edge (`-mr-1.5`). */}
      <div className="-ml-1.5 flex flex-wrap items-center gap-x-0.5 gap-y-1">
        {rows.map((f, i) => (
          <PropertyChip key={`${f.label}-${i}`} field={f} />
        ))}
      </div>
      {statuses.length ? (
        <div className="flex flex-col gap-1 pl-1.5">
          {statuses.map((f, i) => (
            <div key={`status-${f.label}-${i}`}>{f.status}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
  if (label == null) return chips;
  return (
    <div className="flex gap-4">
      <span className="w-24 shrink-0 pt-1.5 type-subtext leading-ui text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0 flex-1">{chips}</div>
    </div>
  );
}

/**
 * One property chip. Editable → the caller's compact control behind a leading
 * glyph, on a quiet hover fill so the whole chip reads as one target. Read-only
 * → the value (a badge, a link, or plain text), or the field's label muted when
 * it is unset, which is the affordance to fill it.
 *
 * THE GLYPH NAMES ITSELF ON HOVER. The chip row's whole economy is that it drops
 * the labels — a `PeekDialogFields` table spends a 96px column saying "Due", the
 * chip row spends a 14px calendar — and that trade only works if the label is
 * one hover away. Without it the row degrades to a rebus: measured on the task
 * specimen, "⊙ 3" (the Comments vital, which falls through `propertyGlyph` to
 * the neutral circle) is unreadable, and a person glyph beside "Alan Turing"
 * cannot say whether he is the Assignee, the Reviewer or the Owner.
 *
 * THE TRIGGER IS THE GLYPH, NOT THE CHIP (Matt, 2026-07-22: "the tooltip should
 * be centered over the icon and not the entire thing ... not the text. Just the
 * icon"). It first shipped as the whole chip, on the theory that a 14px target
 * is hard to hit — but a tooltip anchors to its trigger, so a chip-wide trigger
 * centres the bubble over the chip's MIDDLE, which is the value. That reads as a
 * label for the text it is floating over, and the bubble drifts a different
 * distance on every chip because chips are different widths. Anchored to the
 * glyph it is always in the same place relative to the thing it explains, which
 * is also the honest scope: it explains the ICON.
 *
 * So a chip with no type glyph gets no bubble, and that is the right answer
 * rather than a gap — the mark it leads with instead is an intent DOT (a state
 * chip, a vital with a health colour), and the dot is not a symbol standing in
 * for a word: the word is written right beside it ("In progress").
 */
function PropertyChip({ field: f }: { field: PeekField }) {
  // The leading mark is EITHER the type glyph OR a status/intent dot, never both
  // — a chip that already leads with a colored dot (a state badge, a vital with
  // an intent) doesn't also get a generic circle beside it.
  //
  // The tooltip is skipped for the one chip already showing its label as its
  // BODY (unset + read-only, e.g. a muted "Owner"): a bubble repeating a word
  // 6px to its right is not help. Everywhere else the glyph is the only place
  // the property's name exists.
  const mark =
    f.icon && f.badge == null && f.intent == null ? (
      <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-3.5">
        {f.icon}
      </span>
    ) : null;
  const namesItsOwnBody =
    f.control == null && f.badge == null && (f.value == null || f.value === "");
  const glyph =
    mark == null || namesItsOwnBody ? (
      mark
    ) : (
      <IconTooltip label={f.label}>{mark}</IconTooltip>
    );

  if (f.control != null) {
    // Same box as a read chip (h-7, px-1.5, gap-1.5) so an editable chip and a
    // read chip on the same row sit on one baseline and space evenly — the
    // control inside is borderless with no padding of its own (chip mode), so
    // the chip's own padding is the only padding.
    return (
      <span className="inline-flex h-7 min-w-0 max-w-full items-center gap-1.5 rounded-md px-1.5 hover:bg-muted/60">
        {glyph}
        <span className="flex min-w-0 items-center">{f.control}</span>
      </span>
    );
  }

  const hasValue = f.badge != null || (f.value != null && f.value !== "");
  const body = f.badge ? (
    // A state chip is a dot + label, NOT a filled IntentBadge pill — the
    // reference row (Matt, 2026-07-21) reads status as one more borderless chip
    // beside the others, and a solid pill in a row of quiet chips shouts. The
    // intent still speaks through the dot's colour.
    <span className="flex min-w-0 items-center gap-1.5">
      <IntentDot intent={f.badge.intent} size="xs" />
      <span className="truncate">{f.badge.label}</span>
    </span>
  ) : hasValue ? (
    <span className="flex min-w-0 items-center gap-1.5">
      {f.intent ? <IntentDot intent={f.intent} size="xs" /> : null}
      {f.href ? (
        <NextLink href={f.href} className="min-w-0 truncate hover:underline">
          {f.value}
        </NextLink>
      ) : (
        <span className="min-w-0 truncate">{f.value}</span>
      )}
      {f.hint ? (
        <span className="type-subtext text-muted-foreground">{f.hint}</span>
      ) : null}
    </span>
  ) : (
    // Unset read-only → the label is the affordance ("Owner"), muted.
    <span className="truncate text-muted-foreground">{f.label}</span>
  );

  return (
    <span className="inline-flex h-7 min-w-0 max-w-full items-center gap-1.5 rounded-md px-1.5 type-body text-foreground">
      {glyph}
      {body}
    </span>
  );
}

/** One event of the peek timeline — pre-resolved display data, never a raw envelope. */
export interface PeekTimelineEvent {
  uid: string;
  /** Friendly verb label ("Moved to review") — NOT a raw event type. */
  label: string;
  /** One-line payload summary, composed server-side. */
  summary?: string | null;
  /** Pre-resolved actor display name; null/absent = system. */
  actorLabel?: string | null;
  occurredAt: string;
  /** Lifecycle-critical events get the emphasized dot. */
  critical?: boolean;
}

/**
 * The glyph for one event, chosen from the humanized verb label the loader
 * already composes ("Created", "Stage moved", …) — the same vocabulary
 * `EVENT_VERB_LABEL` emits. Keyed off the LABEL rather than a new contract
 * field on purpose: an icon is presentation, and a wire field would freeze one
 * block's styling choice into the DTO every peek and page has to serialize.
 * An unmapped verb falls through to the neutral dot, so a newly-cataloged
 * event is never iconless.
 */
const TIMELINE_ICONS: Readonly<Record<string, React.ComponentType<{ className?: string }>>> = {
  Created: CirclePlus,
  Applied: CirclePlus,
  Added: CirclePlus,
  "Note added": MessageSquare,
  Updated: PencilLine,
  "Status changed": CircleDot,
  "State changed": CircleDot,
  "Stage moved": ArrowRightCircle,
  Moved: ArrowRightCircle,
  Promoted: ArrowRightCircle,
  Fulfilled: CircleCheck,
  Completed: CircleCheck,
  Verified: CircleCheck,
  Shipped: CircleCheck,
  Closed: CircleCheck,
  Cancelled: CircleX,
  Rejected: CircleX,
  Deleted: CircleX,
  Paused: CircleDot,
  Claimed: UserRound,
  Assigned: UserRound,
};

/**
 * PeekDialogTimeline — the peek's history, rendered the way activity reads
 * everywhere else in the app (Matt, 2026-07-21: "the activity log should look
 * like the actual activities like tasks/issues do"): one compact row per event
 * — a glyph, then an ACTOR-LED sentence with the time folded inline as
 * "· Jul 16".
 *
 * What it replaced and why: a left rail of dots on a connecting hairline, with
 * the time right-aligned in its own column and the actor exiled to a second
 * meta line. That treatment cost two lines and ~40px of width per event to say
 * what one line says, and it read as a Gantt artifact rather than a feed — a
 * real problem now that Activity lives in the 288px rail instead of the main
 * column. Actor-first is also the honest ordering: who did it is the thing you
 * scan for, and it was the one fact pushed furthest down.
 */
export function PeekDialogTimeline({
  events,
  total,
  viewAllHref,
  subjectTitle,
  className,
}: {
  /** Newest-first, pre-capped by the caller (the peek shows ~4–5). */
  events: PeekTimelineEvent[];
  /** Full event count; > events.length renders the overflow line. */
  total?: number;
  /** Where "View all" routes (usually the full page); plain text when absent. */
  viewAllHref?: string;
  /**
   * The subject's own title, so a summary that merely REPEATS it can be dropped.
   * A `:created` payload carries the object's name, so the composed summary for
   * a new deal is literally "Platform rollout" — which rendered as "System
   * Platform rollout", a sentence with no verb in it, directly beneath a
   * masthead already showing that name. With the title known, the row falls back
   * to the verb and reads "System created".
   */
  subjectTitle?: string;
  className?: string;
}) {
  if (!events.length) return null;
  const overflow = total != null && total > events.length ? total : null;
  return (
    // ONE gap between every row (Matt, 2026-07-21: "consistent gap spacing
    // between the items"). It was already `gap-3`, but the rows read uneven
    // because the glyph and the first text line were not on the same centre —
    // that is fixed per row below, not here.
    //
    // Back to `gap-3` (12px) on 2026-07-22: the note above says the unevenness
    // was fixed per row, so the 10px was a leftover compensation for a cause
    // that no longer exists — and 10 is the one value in this stack that cannot
    // land on the 4px grid the rest of the peek is measured against.
    <ol className={cn("flex flex-col gap-3", className)}>
      {events.map((e) => {
        const Glyph = TIMELINE_ICONS[e.label] ?? CircleDot;
        // The predicate is always VERB-LED so the row is a sentence: the verb
        // lowercased after the actor ("Matt Waite created"), plus the composed
        // summary when it carries something the verb doesn't ("stage moved →
        // Qualified", "updated Amount → $18,000").
        //
        // A summary that adds NOTHING is dropped, and there are two ways it can
        // add nothing. It can echo the subject's own name — a `:created`
        // payload carries the object's title. Or it can echo the VERB itself:
        // the loader falls back to `summary = label` for an event with no
        // meaningful payload field, so a bare person:created arrived as label
        // "Created" + summary "Created" and rendered "created Created". Both are
        // the same rule — a summary equal to a fact already on the row is not a
        // detail — so both are checked here, the verb match case-insensitively.
        const verb = e.label.toLowerCase();
        const echoesVerb = e.summary?.trim().toLowerCase() === verb;
        const detail =
          e.summary && e.summary !== subjectTitle && !echoesVerb
            ? e.summary
            : null;
        const predicate = detail ? `${verb} ${detail}` : verb;
        return (
          <li key={e.uid} className="flex items-start gap-2">
            {/* The glyph is centred in a line-box the SAME height as the text's
                first 20px line, so a 14px icon sits on the same
                optical centre as the words beside it instead of hanging from the
                line top on a hand-tuned `mt-px` (Matt, 2026-07-21: "proper
                vertical alignment between the icons and the text"). The box is
                fixed-height, so a summary that wraps to a second line keeps the
                glyph on the FIRST line where the actor and verb are. */}
            {/* NO TOOLTIP HERE, deliberately — and this is the one omission a
                future reader is most likely to file as a miss, so: the glyph's
                whole meaning is the verb, and `predicate` opens with that verb
                two millimetres to the right, every row, always. A bubble reading
                "Status changed" over a line reading "Ada Lovelace status changed
                In review → In progress" is a word said twice, and (measured, at
                this row's left gutter) said in a box that hangs off the panel's
                edge over the dimmed page to do it. The glyph is a scanning aid
                for a sentence that is already there; the chips are a replacement
                for a label that isn't. Only the second kind earns a hover. */}
            <span className="flex h-5 shrink-0 items-center">
              <Glyph
                className={cn(
                  "size-3.5",
                  e.critical ? "text-foreground" : "text-muted-foreground/70",
                )}
              />
            </span>
            <p className="min-w-0 flex-1 type-subtext text-muted-foreground">
              {e.actorLabel ? (
                <span className="text-foreground">{e.actorLabel}</span>
              ) : (
                <span className="text-foreground">System</span>
              )}{" "}
              {predicate}
              {" · "}
              <time dateTime={e.occurredAt} className="tabular-nums">
                {formatRelativeTime(e.occurredAt)}
              </time>
            </p>
          </li>
        );
      })}
      {overflow != null ? (
        <li className="pl-5.5">
          {viewAllHref ? (
            <NextLink
              href={viewAllHref}
              className="type-subtext text-muted-foreground hover:text-foreground hover:underline"
            >
              View all {overflow} events
            </NextLink>
          ) : (
            <span className="type-subtext text-muted-foreground">
              View all {overflow} events
            </span>
          )}
        </li>
      ) : null}
    </ol>
  );
}
